import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../app/hooks';
import Select from 'react-select';
import { dropDownSelectionStyles } from '../../common/timeLiveFunction';
import fetchAPI from '../../common/fetchAPI';
import { getBaseUrl } from '../../common/getBaseUrl';

declare const chrome: any;

interface ExtensionManualEntryProps {
  onBackToTimer: () => void;
}

export const ExtensionManualEntry: React.FC<ExtensionManualEntryProps> = ({ onBackToTimer }) => {
  const { sessionAccountEmployeeId, timeZoneId, showClientInTimesheet, showCostCenterInTimeSheet, showWorkTypeInTimeSheet } = useAppSelector((state) => state.globalState);
  const { defaultWorkTypeId } = useAppSelector((state) => state.timesheet);

  const showClientFlag: boolean = Boolean(showClientInTimesheet);
  const showCostCenterFlag: boolean = Boolean(showCostCenterInTimeSheet);
  const showWorkTypeFlag: boolean = Boolean(showWorkTypeInTimeSheet);

  const [activeEmpId, setActiveEmpId] = useState<number | string | null>(null);
  const [isClientVisible, setIsClientVisible] = useState<boolean>(showClientFlag);
  const [isCostCenterVisible, setIsCostCenterVisible] = useState<boolean>(showCostCenterFlag);
  const [isWorkTypeVisible, setIsWorkTypeVisible] = useState<boolean>(showWorkTypeFlag);

  const [isStorageLoaded, setIsStorageLoaded] = useState<boolean>(false);

  const [clientOptions, setClientOptions] = useState<{ value: any; label: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ value: any; label: string }[]>([]);
  const [taskOptions, setTaskOptions] = useState<{ value: any; label: string }[]>([]);
  const [costCenterOptions, setCostCenterOptions] = useState<{ value: any; label: string }[]>([]);
  const [workTypeOptions, setWorkTypeOptions] = useState<{ value: any; label: string }[]>([]);

  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [selectedCostCenter, setSelectedCostCenter] = useState<any>(null);
  const [selectedWorkType, setSelectedWorkType] = useState<any>(null);
  const [timeError, setTimeError] = useState<any>('');

  const [savedIds, setSavedIds] = useState<{
    clientId?: any;
    projectId?: any;
    taskId?: any;
    costCenterId?: any;
    workTypeId?: any;
  }>({});

  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingCostCenters, setLoadingCostCenters] = useState(false);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [entryDate, setEntryDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [hours, setHours] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const persistStorage = (dataToSave: Record<string, any>) => {
    if (typeof chrome !== 'undefined' && chrome.storage) {
      chrome.storage.local.set(dataToSave);
    } else {
      Object.entries(dataToSave).forEach(([k, v]) => {
        if (v === null || v === undefined) localStorage.removeItem(k);
        else localStorage.setItem(k, JSON.stringify(v));
      });
    }
  };


  useEffect(() => {
    const keysToFetch = [
      'sessionAccountEmployeeId',
      'accountEmployeeId',
      'showClientInTimesheet',
      'showCostCenterInTimeSheet',
      'showWorkTypeInTimeSheet',
      'manual_clientId',
      'manual_projectId',
      'manual_taskId',
      'manual_costCenterId',
      'manual_workTypeId'
    ];

    if (typeof chrome !== 'undefined' && chrome.storage) {
      chrome.storage.local.get(keysToFetch, (result: any) => {
        const resolvedEmpId = result?.accountEmployeeId || result?.sessionAccountEmployeeId || sessionAccountEmployeeId;
        if (resolvedEmpId) setActiveEmpId(Number(resolvedEmpId));

        if (result?.showClientInTimesheet !== undefined) setIsClientVisible(Boolean(result.showClientInTimesheet));
        if (result?.showCostCenterInTimeSheet !== undefined) setIsCostCenterVisible(Boolean(result.showCostCenterInTimeSheet));
        if (result?.showWorkTypeInTimeSheet !== undefined) setIsWorkTypeVisible(Boolean(result.showWorkTypeInTimeSheet));

        setSavedIds({
          clientId: result?.manual_clientId ?? null,
          projectId: result?.manual_projectId ?? null,
          taskId: result?.manual_taskId ?? null,
          costCenterId: result?.manual_costCenterId ?? null,
          workTypeId: result?.manual_workTypeId ?? null,
        });

        setIsStorageLoaded(true);
      });
    } else {
      if (sessionAccountEmployeeId) setActiveEmpId(Number(sessionAccountEmployeeId));
      const parseLocal = (k: string) => {
        const val = localStorage.getItem(k);
        return val ? JSON.parse(val) : null;
      };
      setSavedIds({
        clientId: parseLocal('manual_clientId'),
        projectId: parseLocal('manual_projectId'),
        taskId: parseLocal('manual_taskId'),
        costCenterId: parseLocal('manual_costCenterId'),
        workTypeId: parseLocal('manual_workTypeId'),
      });
      setIsStorageLoaded(true);
    }
  }, [sessionAccountEmployeeId]);

  const handleClearAll = () => {
    setSelectedClient(null);
    setSelectedProject(null);
    setSelectedTask(null);
    setSelectedCostCenter(null);
    setSelectedWorkType(null);
    setHours('');
    setTimeError('');
    setDescription('');
    setSavedIds({});

    persistStorage({
      manual_clientId: null,
      manual_projectId: null,
      manual_taskId: null,
      manual_costCenterId: null,
      manual_workTypeId: null,
    });
  };

  const handleClientChange = (opt: any) => {
    setSelectedClient(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, clientId: val, projectId: null, taskId: null, costCenterId: null }));
    persistStorage({
      manual_clientId: val,
      manual_projectId: null,
      manual_taskId: null,
      manual_costCenterId: null,
    });

    if (!opt) {
      setSelectedProject(null);
      setSelectedTask(null);
      setSelectedCostCenter(null);
    }
  };

  const handleProjectChange = (opt: any) => {
    setSelectedProject(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, projectId: val, taskId: null, costCenterId: null }));
    persistStorage({
      manual_projectId: val,
      manual_taskId: null,
      manual_costCenterId: null,
    });

    if (!opt) {
      setSelectedTask(null);
      setSelectedCostCenter(null);
    }
  };

  const handleTaskChange = (opt: any) => {
    setSelectedTask(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, taskId: val, costCenterId: null }));
    persistStorage({
      manual_taskId: val,
      manual_costCenterId: null,
    });

    if (!opt) {
      setSelectedCostCenter(null);
    }
  };

  const handleCostCenterChange = (opt: any) => {
    setSelectedCostCenter(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, costCenterId: val }));
    persistStorage({ manual_costCenterId: val });
  };

  const handleWorkTypeChange = (opt: any) => {
    setSelectedWorkType(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, workTypeId: val }));
    persistStorage({ manual_workTypeId: val });
  };

 
  useEffect(() => {
    if (!isStorageLoaded || !activeEmpId) return;

    if (isClientVisible) {
      setLoadingClients(true);
      fetchAPI(`api/Timesheets/GetAccountClientForTimesheet/${activeEmpId}/0`, 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.data || [];
          const formatted = list.map((item: any) => ({
            value: item.AccountClientId,
            label: item.ClientName,
          }));
          setClientOptions(formatted);

          const targetId = savedIds.clientId;
          if (targetId) {
            const match = formatted.find((c: any) => c.value === targetId);
            if (match) setSelectedClient(match);
          }
        })
        .catch((err) => console.error('Error fetching clients:', err))
        .finally(() => setLoadingClients(false));
    } else {
      setLoadingProjects(true);
      fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/0/${activeEmpId}`, 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.data || [];
          const formatted = list.map((item: any) => ({
            value: item.AccountProjectId,
            label: item.ProjectName,
          }));
          setProjectOptions(formatted);

          const targetId = savedIds.projectId;
          if (targetId) {
            const match = formatted.find((p: any) => p.value === targetId);
            if (match) setSelectedProject(match);
          }
        })
        .catch((err) => console.error('Error fetching projects:', err))
        .finally(() => setLoadingProjects(false));
    }

    if (isWorkTypeVisible) {
      setLoadingWorkTypes(true);
      fetchAPI('api/WorkTypes', 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.data || [];
          const formatted = list.map((item: any) => ({
            value: item.AccountWorkTypeId,
            label: item.AccountWorkType,
          }));
          setWorkTypeOptions(formatted);

          const targetId = savedIds.workTypeId;
          if (targetId) {
            const match = formatted.find((w: any) => w.value === targetId);
            if (match) setSelectedWorkType(match);
          }
        })
        .catch((err) => console.error('Error fetching work types:', err))
        .finally(() => setLoadingWorkTypes(false));
    }
  }, [isStorageLoaded, activeEmpId, isClientVisible, isWorkTypeVisible]);

  useEffect(() => {
    if (!isClientVisible || !selectedClient?.value || !activeEmpId) {
      if (isClientVisible && !selectedClient) setProjectOptions([]);
      return;
    }

    setLoadingProjects(true);
    fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/${selectedClient.value}/${activeEmpId}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const formatted = list.map((item: any) => ({
          value: item.AccountProjectId,
          label: item.ProjectName,
        }));
        setProjectOptions(formatted);

        const targetId = savedIds.projectId;
        if (targetId) {
          const match = formatted.find((p: any) => p.value === targetId);
          if (match) setSelectedProject(match);
        }
      })
      .catch((err) => console.error('Error fetching projects:', err))
      .finally(() => setLoadingProjects(false));
  }, [selectedClient, activeEmpId, isClientVisible]);


  useEffect(() => {
    if (!selectedProject?.value || !activeEmpId) {
      setTaskOptions([]);
      return;
    }

    setLoadingTasks(true);
    fetchAPI(`api/Timesheets/GetAccountProjectTasksForTimesheet/${selectedProject.value}/${activeEmpId}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const formatted = list.map((item: any) => ({
          value: item.AccountProjectTaskId,
          label: item.TaskName,
        }));
        setTaskOptions(formatted);

        const targetId = savedIds.taskId;
        if (targetId) {
          const match = formatted.find((t: any) => t.value === targetId);
          if (match) setSelectedTask(match);
        }
      })
      .catch((err) => console.error('Error fetching tasks:', err))
      .finally(() => setLoadingTasks(false));
  }, [selectedProject, activeEmpId]);

  useEffect(() => {
    if (!isCostCenterVisible || !selectedTask?.value || !selectedProject?.value || !activeEmpId) {
      setCostCenterOptions([]);
      return;
    }

    setLoadingCostCenters(true);
    fetchAPI(`api/v2/CostCenter/GetAccountCostCenterForTimesheet/${activeEmpId}/${selectedProject.value}/${selectedTask.value}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const formatted = list.map((item: any) => ({
          value: item.AccountCostCenterId,
          label: item.AccountCostCenter,
        }));
        setCostCenterOptions(formatted);

        const targetId = savedIds.costCenterId;
        if (targetId) {
          const match = formatted.find((c: any) => c.value === targetId);
          if (match) setSelectedCostCenter(match);
        }
      })
      .catch((err) => console.error('Error fetching cost centers:', err))
      .finally(() => setLoadingCostCenters(false));
  }, [selectedTask, selectedProject, activeEmpId, isCostCenterVisible]);

  const convertTimeToDecimal = (timeStr: string): number => {
    if (!timeStr || !timeStr.includes(':')) return Number(timeStr) || 0;
    const [h, m] = timeStr.split(':').map(Number);
    return Number((h + m / 60).toFixed(2));
  };

const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>, setHours: (val: string) => void) => {
  let value = e.target.value.replace(/\D/g, '');
  if (value.length > 4) value = value.slice(0, 4);

  let formattedValue = value;
  if (value.length >= 3) {
    formattedValue = `${value.slice(0, 2)}:${value.slice(2)}`;
  }
  setHours(formattedValue);
  if (formattedValue.length === 5) {
    const [h, m] = formattedValue.split(':').map(Number);

    if (h >= 24) {
      setTimeError(`${entryDate} ${formattedValue} Invalid Time! Time must be less than 24:00`);
    } else if (m >= 60) {
      setTimeError('Invalid Time! Minutes must be less than 60');
    } else {
      setTimeError('');
    }
  } else {
    setTimeError('');
  }
};

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (allowedKeys.includes(e.key)) return;
    if (!/^\d$/.test(e.key)) e.preventDefault();
  };

  const handleSaveTimeEntry = async () => {
    if (timeError || !hours) {
      alert(timeError || 'Please enter valid hours.');
      return;
    }
    const hoursDecimal = convertTimeToDecimal(hours);

    if (hoursDecimal <= 0) {
      alert('Please enter valid hours.');
      return;
    }

    setIsSaving(true);
    try {
      let periodId = 0;
      let periodStartDate = entryDate;
      let periodEndDate = entryDate;
      let timesheetPeriodType = 'Weekly';

      try {
        const periodRes = await fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${entryDate}`, 'GET', '', true);
        if (periodRes.status === 200) {
          const periodJson = await periodRes.json();
          const targetData = periodJson?.data || periodJson;
          const periodObj = targetData?.AccountEmployeeTimeEntryPeriod || {};

          periodId = periodObj?.AccountEmployeeTimeEntryPeriodId || null;
          periodStartDate = periodObj?.TimeEntryStartDate || entryDate;
          periodEndDate = periodObj?.TimeEntryEndDate || entryDate;
          timesheetPeriodType = periodObj?.TimeEntryViewType || 'Weekly';
        }
      } catch (periodErr) {
        console.warn('Could not fetch period info, proceeding with defaults:', periodErr);
      }

      const postObject = {
        AccountEmployeeTimeEntryPeriodId: periodId,
        AccountEmployeeTimeEntryApprovalProjectId: null,
        AccountEmployeeTimeEntryId: 0,
        AccountEmployeeId: Number(activeEmpId),
        AccountClientId: isClientVisible ? selectedClient?.value || 0 : 0,
        AccountProjectId: selectedProject?.value || 0,
        AccountProjectTaskId: selectedTask?.value || 0,
        AccountCostCenterId: isCostCenterVisible ? selectedCostCenter?.value || 0 : 0,
        AccountWorkTypeId: isWorkTypeVisible ? selectedWorkType?.value || defaultWorkTypeId || 0 : defaultWorkTypeId || 0,
        AccountTimeOffTypeId: null,
        Hours: hoursDecimal,
        Percentage: null,
        PeriodStartDate: periodStartDate,
        PeriodEndDate: periodEndDate,
        TimeEntryDate: entryDate,
        StartTime: null,
        EndTime: null,
        IsTimeOff: false,
        TimesheetPeriodType: timesheetPeriodType,
        IsSubmitted: false,
        Description: description,
        Reason: '',
        TimeZoneId: timeZoneId,
        showClockStartEnd: false,
        SystemPlatformSourceId: '50E254BE-D515-4FBA-BFB7-DFD27852D023',
      };

      const response = await fetchAPI('api/Timesheets/AddAccountEmployeeTimeEntry', 'POST', postObject, true);
      if (response.ok) {
        alert('Time Entry Added Successfully!');
        setHours('');
        setDescription('');
        onBackToTimer();
      } else {
        const errData = await response.json().catch(() => null);
        alert(errData?.message || 'Failed to save time entry.');
      }
    } catch (error) {
      console.error('Error saving time entry:', error);
      alert('An error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column">
      <button
        type="button"
        onClick={onBackToTimer}
        className="btn btn-link btn-color-primary p-0 fs-8 fw-bold mb-3 text-decoration-none d-flex align-items-center me-auto"
      >
        <i className="bi bi-arrow-left fs-7 me-1"></i> Back to Track Time
      </button>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bolder text-gray-900 fs-7 m-0">Enter Time Manually</h6>
        {(selectedClient || selectedProject || selectedTask || selectedCostCenter || selectedWorkType || hours || description) && (
          <button
            type="button"
            onClick={handleClearAll}
            className="btn btn-icon btn-active-light-danger btn-sm p-1 border-0"
            title="Clear all dropdowns and fields"
            style={{ width: '28px', height: '28px' }}
          >
            <i className="bi bi-arrow-counterclockwise fs-7 text-danger"></i>
          </button>
        )}
      </div>

      {isClientVisible && (
        <div className="mb-3">
          <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Client</label>
          <Select
            value={selectedClient}
            onChange={handleClientChange}
            options={clientOptions}
            isLoading={loadingClients}
            placeholder="Select Client"
            styles={dropDownSelectionStyles}
          />
        </div>
      )}

      <div className="mb-3">
        <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Project</label>
        <Select
          value={selectedProject}
          onChange={handleProjectChange}
          options={projectOptions}
          isLoading={loadingProjects}
          isDisabled={isClientVisible && !selectedClient}
          placeholder="Select Project"
          styles={dropDownSelectionStyles}
        />
      </div>

      <div className="mb-3">
        <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Task</label>
        <Select
          value={selectedTask}
          onChange={handleTaskChange}
          options={taskOptions}
          isLoading={loadingTasks}
          isDisabled={!selectedProject}
          placeholder="Select Task"
          styles={dropDownSelectionStyles}
        />
      </div>

      {isCostCenterVisible && (
        <div className="mb-3">
          <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Cost Center</label>
          <Select
            value={selectedCostCenter}
            onChange={handleCostCenterChange}
            options={costCenterOptions}
            isLoading={loadingCostCenters}
            isDisabled={!selectedTask}
            placeholder="Select Cost Center"
            styles={dropDownSelectionStyles}
          />
        </div>
      )}

      {isWorkTypeVisible && (
        <div className="mb-3">
          <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Work Type</label>
          <Select
            value={selectedWorkType}
            onChange={handleWorkTypeChange}
            options={workTypeOptions}
            isLoading={loadingWorkTypes}
            placeholder="Select Work Type"
            styles={dropDownSelectionStyles}
          />
        </div>
      )}

      <div className="mb-3">
        <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Date</label>
        <input
          type="date"
          className="form-control form-control-sm fs-8"
          value={entryDate}
          onChange={(e) => setEntryDate(e.target.value)}
        />
      </div>

      {/* <div className="mb-3">
        <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Hours</label>
        <input
          type="text"
          className="form-control form-control-sm fs-8"
          placeholder="00:00"
          maxLength={5}
          value={hours}
          onChange={(e) => handleTimeChange(e, setHours)}
          onKeyDown={handleKeyDown}
        />
      </div> */}
<div className="mb-3">
  <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Hours</label>
  <input
    type="text"
    className={`form-control form-control-sm fs-8 ${timeError ? 'is-invalid' : ''}`}
    placeholder="00:00"
    maxLength={5}
    value={hours}
    onChange={(e) => handleTimeChange(e, setHours)}
    onKeyDown={handleKeyDown}
  />
  {timeError && (
    <div className="text-danger fs-9 mt-1 fw-semibold">
      {timeError}
    </div>
  )}
</div>

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <label className="form-label fs-8 fw-semibold text-gray-600 m-0">Description</label>
        </div>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 500))}
          className="form-control form-control-sm fs-8"
          rows={3}
          placeholder="What did you work on?"
        />
      </div>

      <button
  type="button"
  disabled={!selectedTask || isSaving || Boolean(timeError) || !hours}
  onClick={handleSaveTimeEntry}
  className="btn btn-extension-primary btn-sm w-100 fs-7 fw-bold py-2 mb-2"
>
  {isSaving ? 'Saving...' : 'Save Time Entry'}
</button>
    </div>
  );
};

export default ExtensionManualEntry;
